<script lang="ts">
    import { onMount, createEventDispatcher } from 'svelte';
    import { Line } from 'svelte-chartjs';
    import {
        Chart as ChartJS,
        CategoryScale,
        LinearScale,
        PointElement,
        LineElement,
        Title,
        Tooltip,
        Legend,
        Filler,
        TimeScale
    } from 'chart.js';
    import 'chartjs-adapter-date-fns';
    import { ja, tr } from 'date-fns/locale';
    import { format } from 'date-fns';
    import { browser } from '$app/environment';

    ChartJS.register(
        CategoryScale,
        LinearScale,
        PointElement,
        LineElement,
        Title,
        Tooltip,
        Legend,
        Filler,
        TimeScale
    );

    export let data: any;
    export let labels: any;
    export let videos: any;

    const dispatch = createEventDispatcher();
    let selectedMonth = Object.keys(data)[0];

    $: chartData = {
        labels: labels[selectedMonth],
        datasets: [{
            label: '同時接続数',
            data: data[selectedMonth],
            fill: true,
            backgroundColor: 'rgba(99, 102, 241, 0.1)',
            borderColor: 'rgba(99, 102, 241, 0.8)',
            borderWidth: 2,
            tension: 0.1,
            pointRadius: 0,
            pointHoverRadius: 6,
            pointHoverBackgroundColor: 'rgba(99, 102, 241, 1)',
            pointHoverBorderColor: '#fff',
            pointHoverBorderWidth: 2,
            stepped: false,
            cubicInterpolationMode: 'monotone'
        }]
    };

    let isDarkMode = false;

    // 初期値の設定
    if (browser) {
        const darkMode = localStorage.getItem('darkMode');
        isDarkMode = darkMode === 'true' || (!darkMode && window.matchMedia('(prefers-color-scheme: dark)').matches);
    }

    let maxTicksLimit = 12;  // デフォルト値

    onMount(() => {
        const updateMode = () => {
            isDarkMode = document.documentElement.classList.contains('dark');
        };
        
        // ダークモード変更を監視
        const observer = new MutationObserver(updateMode);
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class']
        });

        if (browser) {
            maxTicksLimit = window.innerWidth < 640 ? 6 : 12;
        }

        return () => observer.disconnect();
    });

    $: options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            intersect: false,
            mode: 'index'
        },
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                padding: 12,
                titleFont: {
                    size: 13,
                    weight: '500'
                },
                bodyFont: {
                    size: 12
                },
                bodySpacing: 8,
                cornerRadius: 8,
                displayColors: false,
                callbacks: {
                    title: function(tooltipItems) {
                        try {
                            if (!tooltipItems || tooltipItems.length === 0) return '';
                            
                            const rawDate = tooltipItems[0].label;
                            if (!rawDate) return '';
                            
                            const date = new Date(rawDate);
                            if (isNaN(date.getTime())) return rawDate;
                            
                            return format(date, 'yyyy年MM月dd日');
                        } catch (e) {
                            console.error('Date parsing error:', e);
                            return '';
                        }
                    },
                    label: function(context) {
                        const value = context.parsed.y;
                        return `同時接続数: ${value.toLocaleString()}人`;
                    }
                }
            }
        },
        scales: {
            x: {
                type: 'time',
                adapters: {
                    date: {
                        locale: ja
                    }
                },
                time: {
                    unit: 'month',
                    displayFormats: {
                        month: 'yyyy年M月'
                    }
                },
                grid: {
                    color: (context) => {
                        const date = new Date(context.tick.value);
                        return date.getDate() === 1 ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.05)';
                    },
                    display: false
                },
                ticks: {
                    font: {
                        size: 11
                    },
                    color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : '#111111',
                    maxRotation: 45,
                    minRotation: 45,
                    autoSkip: false,
                    maxTicksLimit: maxTicksLimit
                }
            },
            y: {
                beginAtZero: true,
                grid: {
                    color: 'rgba(0, 0, 0, 0.05)',
                    drawBorder: false
                },
                ticks: {
                    font: {
                        size: 11
                    },
                    color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : '#111111',
                    padding: 8,
                    callback: function(value) {
                        return value.toLocaleString();
                    },
                    display: true
                }
            }
        },
        elements: {
            line: {
                tension: 0.1
            },
            point: {
                radius: 0,
                hoverRadius: 6,
                hoverBackgroundColor: 'rgba(99, 102, 241, 1)',
                hoverBorderColor: '#fff',
                hoverBorderWidth: 2
            }
        }
    };
</script>

<div class="bg-zinc-950/[0.02] dark:bg-white/[0.02] backdrop-blur-sm border border-black/[0.05] dark:border-white/[0.05] rounded-2xl p-6">
    <div class="flex items-center justify-between mb-6">
        <h3 class="text-lg font-medium tracking-wide text-black/80 dark:text-white/80">視聴者数の推移</h3>
    </div>
    
    <div class="h-[300px]">
        <Line data={chartData} {options} />
    </div>
</div>

<style>
    :global(.chartjs-tooltip) {
        @apply shadow-lg;
    }
</style>
